import { CommonModule } from '@angular/common';
import { Component, Output, EventEmitter, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SubscriptionComparisonComponent } from '../../subscription/subscription-comparison/subscription-comparison.component';
import { MixpanelService } from 'src/app/core/services/mixpanel/mixpanel.service';
import { SubscriptionService } from 'src/app/core/services/subscription/subscription.service';
import { ModalService } from 'src/app/core/services/modal/modal.service';

/**
 * UpgradeModalComponent
 *
 * Modal that displays subscription comparison and prompts users to upgrade
 * to Game Master tier. Triggered by the Upgrade! button in the header.
 *
 * @example
 * <app-upgrade-modal />
 */
@Component({
  selector: 'app-upgrade-modal',
  standalone: true,
  imports: [CommonModule, SubscriptionComparisonComponent],
  templateUrl: './upgrade-modal.component.html',
  styleUrls: ['./upgrade-modal.component.scss'],
})
export class UpgradeModalComponent {
  private mixPanelService = inject(MixpanelService);
  private subscriptionService = inject(SubscriptionService);
  private router = inject(Router);
  private modalService = inject(ModalService);

  /**
   * Whether the modal is open
   */
  isOpen = false;

  /**
   * Emits when the modal should be closed
   */
  @Output() close = new EventEmitter<void>();

  /**
   * Emits when the user clicks the upgrade button
   */
  @Output() upgrade = new EventEmitter<void>();

  constructor() {
    // Subscribe to modal service event
    this.modalService.showUpgradeModal.subscribe(() => {
      this.open();
    });
  }

  /**
   * Opens the modal
   */
  open(): void {
    this.isOpen = true;
    this.mixPanelService.trackEvent('upgrade_modal_opened');
  }

  /**
   * Closes the modal
   */
  onClose(): void {
    this.isOpen = false;
    this.close.emit();
    this.mixPanelService.trackEvent('upgrade_modal_closed');
  }

  /**
   * Handles the upgrade button click
   */
  onUpgrade(): void {
    this.mixPanelService.trackEvent('upgrade_modal_upgrade_clicked');

    // Trigger upgrade flow
    this.subscriptionService.upgradeToGameMaster();

    // Emit to parent component (in case they want custom handling)
    this.upgrade.emit();

    // Close the modal
    this.onClose();
  }
}
